import NumberOptionList from "@/app/components/numberOptionList";
import useAnalyticsManager from "@/app/hooks/useAnalyticsManager";
import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import { getBestPrices } from "@/components/banners/getBestPrice";
import { DeviceType } from "@/types/search";
import { ProductHit } from "@/components/products-tile/product";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { useGetShoppingCartQuery, useModifyShoppingCartMutation } from "@/store/shoppingCartApi";
import { showCartToast } from "@/store/shoppingCartSlice";
import getTrueCategoryFromAlgoliaCategoryList from "@/utils/analytics/getTrueCategoryFromAlgoliaCategoryList";
import { IncomingAnalyticsProduct } from "@/utils/analyticsEventSender";
import delay from "delay";
import { StatusCodes } from "http-status-codes";
import { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import styles from "./productsGrid.module.scss";

type AddToCartProps = {
  isListView: boolean;
  deviceType: DeviceType;
  hit: ProductHit;
  sendEvent: SendEventForHits;
};

const AddToCart = ({ isListView, deviceType, hit }: AddToCartProps) => {
  const [modifyShoppingCartMutation] = useModifyShoppingCartMutation();
  const { dispatchModifyCartEvent } = useAnalyticsManager();
  const dispatch = useAppDispatch();
  const { mp_id: mpId } = hit;
  let productFromServer = useAppSelector((state) => {
    return state.searchSlice.bestPrices[mpId];
  });
  const minQuantity = productFromServer?.minQty || 1;
  const [quantity, setQuantity] = useState(1);
  const { city, zipCode } = useLocationDetails();
  const [quantityChangedByUser, setQuantityChangedByUser] = useState(false);
  const categoryName = useMemo(() => {
    const categoryLevels = Object.values(hit.category);
    const splitCategory = categoryLevels[categoryLevels.length - 1].split(" > ");
    return splitCategory[splitCategory.length - 1];
  }, [hit.category]);

  const { data: shoppingCart } = useGetShoppingCartQuery();
  const productInCart = useMemo(() => {
    const products = !shoppingCart
      ? []
      : shoppingCart.vendorOrders
          .flatMap((vo) => {
            return vo.products;
          })
          .filter((p) => !p.noLongerForSale);
    return products.find((p) => p.mpId === mpId);
  }, [shoppingCart, mpId]);

  useEffect(() => {
    if (productInCart && quantityChangedByUser === false) {
      setQuantity(productInCart?.quantity);
    }
  }, [productInCart, quantityChangedByUser]);

  const [productIsOrWillBeAddedToCart, setProductIsOrWillBeAddedToCart] = useState(false);

  const modifyShoppingCartMutationWrapper = useCallback(
    async (event?: FormEvent<HTMLFormElement>, removeFromCart?: boolean) => {
      if (event) {
        event.preventDefault();
      }
      if (!removeFromCart) {
        setProductIsOrWillBeAddedToCart(true);
      }
      if (!productFromServer || productFromServer.manufacturerProductId !== hit.mp_id) {
        // If the product is not loaded yet, we will debounce the add to cart action
        // to avoid multiple requests and wait for the product data to be available.
        console.log(`Product ${mpId} has not loaded yet, debouncing add to cart...`);
        await delay(500);
        const onDemandMpId = [hit.mp_id];
        const bestPricesLocal = await getBestPrices({
          mpIds: onDemandMpId,
          isBuyGetPage: false,
          tag: "none",
          postalCode: zipCode,
        });
        console.log("bestPrices on demand = ", bestPricesLocal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (bestPricesLocal?.[hit.mp_id]) {
          productFromServer = bestPricesLocal[hit.mp_id];
        }

        modifyShoppingCartMutationWrapper(event, removeFromCart);
        return;
      }
      const quantityCurrentlyInCart = productInCart ? productInCart.quantity : 0;
      const newQuantity = removeFromCart ? 0 : quantity;
      const response = await modifyShoppingCartMutation({
        productsToModify: [
          {
            mpId: mpId,
            vendorProductId: productFromServer.vendorProductId,
            minimumQuantity: productFromServer.minQty,
            quantity: newQuantity,
            categoryName: categoryName,
          },
        ],
        incrementQuantity: false,
      }).unwrap();
      if (response.status === StatusCodes.OK) {
        if (!productInCart && newQuantity > 0) {
          dispatch(showCartToast(`${newQuantity} item${newQuantity > 1 ? "s" : ""} added to cart`));
        } else if (newQuantity === 0 && quantityCurrentlyInCart > 0) {
          dispatch(
            showCartToast({
              message: `${quantityCurrentlyInCart} item${quantityCurrentlyInCart > 1 ? "s" : ""} removed from your cart`,
              type: "success",
              showCheckoutLink: false,
            }),
          );
        }
        const productAddedToCart: IncomingAnalyticsProduct = {
          mpId: hit.mp_id,
          productTitle: hit.title,
          unitPrice: hit.price,
          quantity: quantity,
          originalQuantity: productInCart ? productInCart.quantity : 0,
          brandName: hit.brand,
          categoryList: [],
        };
        // this is for typescript...
        if (productAddedToCart.categoryList) {
          if (hit.category["lvl1"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(hit.category["lvl1"]));
          }
          if (hit.category["lvl2"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(hit.category["lvl2"]));
          }
          if (hit.category["lvl3"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(hit.category["lvl3"]));
          }
          if (hit.category["lvl4"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(hit.category["lvl4"]));
          }
          if (hit.category["lvl5"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(hit.category["lvl5"]));
          }
        }

        dispatchModifyCartEvent([productAddedToCart], "Search Page", hit.__queryID);
      }

      // TODO - Failure condition
      // dispatch(showCartToast(`${quantity} item${quantity > 1 ? "s" : ""} added to cart`));
    },
    [
      categoryName,
      dispatch,
      dispatchModifyCartEvent,
      hit,
      modifyShoppingCartMutation,
      mpId,
      productFromServer,
      productInCart,
      quantity,
    ],
  );
  const debouncedModifyShoppingCartMutation = useDebouncedCallback(modifyShoppingCartMutationWrapper, 500);

  const increaseQuantity = useCallback(() => {
    if (debouncedModifyShoppingCartMutation.isPending()) {
      debouncedModifyShoppingCartMutation.cancel();
    }
    setQuantity(quantity + 1);
    debouncedModifyShoppingCartMutation();
    setQuantityChangedByUser(true);
  }, [debouncedModifyShoppingCartMutation, quantity]);

  const decreaseQuantity = useCallback(async () => {
    if (debouncedModifyShoppingCartMutation.isPending()) {
      debouncedModifyShoppingCartMutation.cancel();
    }
    if (quantity <= minQuantity) {
      setProductIsOrWillBeAddedToCart(false);
      modifyShoppingCartMutationWrapper(undefined, true);
      setQuantity(minQuantity);
    } else {
      setQuantity(quantity - 1);
      debouncedModifyShoppingCartMutation();
    }
    setQuantityChangedByUser(true);
  }, [debouncedModifyShoppingCartMutation, modifyShoppingCartMutationWrapper, quantity, minQuantity]);

  const [numberListStartingPoint, setNumberListStartingPoint] = useState(1);
  const [numberListEndingPoint, setNumberListEndingPoint] = useState(1);

  const onQuantityDropdownHover = useCallback(() => {
    if (productFromServer) {
      setNumberListStartingPoint(productFromServer.minQty);
      setNumberListEndingPoint(productFromServer.inventory > 999 ? 999 : productFromServer.inventory);
    }
  }, [productFromServer]);

  useEffect(() => {
    setProductIsOrWillBeAddedToCart(productInCart ? true : false);
  }, [productInCart]);

  if (!productIsOrWillBeAddedToCart) {
    return (
      <form
        className={`d-flex justify-content-start my-2 ${styles.addToCartForm} ${
          isListView && deviceType === "desktop" ? styles.cartList : ""
        }`}
        onSubmit={modifyShoppingCartMutationWrapper}
      >
        <select
          id="productQuantity"
          aria-label="quantity"
          className={`${styles.quantity} form-control`}
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          onMouseOver={onQuantityDropdownHover}
          {...(hit.isSponsored
            ? { "data-ts-product": hit.mp_id, "data-ts-clickable": true, "data-ts-resolved-bid": hit.resolvedBidId }
            : {})}
        >
          <NumberOptionList start={numberListStartingPoint} end={numberListEndingPoint} />
        </select>
        <button
          type="submit"
          className={`${styles.addToCart} w-100`}
          {...(hit.isSponsored
            ? { "data-ts-product": hit.mp_id, "data-ts-clickable": true, "data-ts-resolved-bid": hit.resolvedBidId }
            : {})}
        >
          Add to Cart
        </button>
      </form>
    );
  } else {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center my-2">
          <button
            className={`btn btn-secondary ${styles.addRemoveButton}`}
            onClick={decreaseQuantity}
            {...(hit.isSponsored
              ? { "data-ts-product": hit.mp_id, "data-ts-clickable": true, "data-ts-resolved-bid": hit.resolvedBidId }
              : {})}
          >
            -
          </button>
          {quantity}
          <button
            disabled={productFromServer && quantity >= productFromServer.inventory ? true : false}
            className={`btn btn-secondary ${styles.addRemoveButton}`}
            onClick={increaseQuantity}
            {...(hit.isSponsored
              ? { "data-ts-product": hit.mp_id, "data-ts-clickable": true, "data-ts-resolved-bid": hit.resolvedBidId }
              : {})}
          >
            +
          </button>
        </div>
      </div>
    );
  }
};

export default AddToCart;
